// src/services/tutorMatchmakingService.ts

import { supabase } from 'src/lib/supabaseClient';
import {
  TutorMatchCriteria,
  TutorMatchScoreBreakdown,
  RankedTutorMatch,
  MatchmakerTutorCandidate,
} from 'src/types/tutorMatchmaking';

export const DEFAULT_MAX_BATCH_CAPACITY = 8;

/**
 * Normalizes text for clean token comparisons.
 */
function normalizeString(text?: string | null): string {
  if (!text) return '';
  return text.toLowerCase().trim().replace(/[^a-z0-9]/g, ' ');
}

/**
 * Extracts a normalized array of subject strings from a tutor's diverse data formats.
 */
function extractTutorSubjects(tutor: MatchmakerTutorCandidate): string[] {
  const subjects: string[] = [];

  if (Array.isArray(tutor.subjects)) {
    subjects.push(...tutor.subjects);
  }

  if (Array.isArray(tutor.subjects_taught)) {
    subjects.push(...tutor.subjects_taught);
  } else if (typeof tutor.subjects_taught === 'string') {
    try {
      const parsed = JSON.parse(tutor.subjects_taught);
      if (Array.isArray(parsed)) {
        subjects.push(...parsed);
      } else {
        subjects.push(tutor.subjects_taught);
      }
    } catch {
      // Split by comma if plain string
      subjects.push(...tutor.subjects_taught.split(',').map((s) => s.trim()));
    }
  }

  return subjects.map((s) => s.trim()).filter(Boolean);
}

/**
 * Calculates subject alignment (Weight: 40%).
 * Evaluates exact name match, partial matches, education fields, and bio keywords.
 */
export function calculateSubjectScore(
  targetSubject: string,
  tutor: MatchmakerTutorCandidate
): { score: number; detail: string } {
  const targetNorm = normalizeString(targetSubject);
  if (!targetNorm) {
    return { score: 100, detail: 'No subject filter specified' };
  }

  const tutorSubjects = extractTutorSubjects(tutor);
  const normalizedTutorSubjects = tutorSubjects.map(normalizeString);

  // 1. Direct exact or substring match in listed subjects
  const hasExactSubject = normalizedTutorSubjects.some(
    (s) => s === targetNorm || s.includes(targetNorm) || targetNorm.includes(s)
  );

  if (hasExactSubject) {
    return {
      score: 100,
      detail: `Direct match for subject "${targetSubject}"`,
    };
  }

  // 2. Check academic education fields (e.g., M.Sc Physics)
  const educationFields = (tutor.education || [])
    .map((e) => normalizeString(`${e.field || ''} ${e.degree || ''}`))
    .join(' ');

  if (educationFields.includes(targetNorm)) {
    return {
      score: 80,
      detail: `Degree or specialization aligns with "${targetSubject}"`,
    };
  }

  // 3. Check bio text for mentions
  const bioNorm = normalizeString(tutor.bio);
  if (bioNorm.includes(targetNorm)) {
    return {
      score: 65,
      detail: `Mentions "${targetSubject}" in professional bio`,
    };
  }

  // 4. Token overlap
  const targetTokens = targetNorm.split(/\s+/).filter((t) => t.length > 2);
  const anyTokenMatch = targetTokens.some((token) =>
    normalizedTutorSubjects.some((s) => s.includes(token)) ||
    educationFields.includes(token) ||
    bioNorm.includes(token)
  );

  if (anyTokenMatch) {
    return {
      score: 40,
      detail: `Related topical keywords found for "${targetSubject}"`,
    };
  }

  return {
    score: 0,
    detail: `No alignment found for subject "${targetSubject}"`,
  };
}

/**
 * Calculates timezone offset in hours between two IANA timezone strings.
 * Uses standard Intl.DateTimeFormat (cross-platform, browser & Node.js).
 */
export function calculateTimezoneOffsetHours(tz1?: string | null, tz2?: string | null): number {
  if (!tz1 || !tz2) return 0;
  if (tz1.trim().toLowerCase() === tz2.trim().toLowerCase()) return 0;

  try {
    const now = new Date();
    const formatter = (timeZone: string) =>
      new Intl.DateTimeFormat('en-US', {
        timeZone,
        hour: 'numeric',
        minute: 'numeric',
        hour12: false,
      });

    const parts1 = formatter(tz1).formatToParts(now);
    const parts2 = formatter(tz2).formatToParts(now);

    const hour1 = parseInt(parts1.find((p) => p.type === 'hour')?.value || '0', 10);
    const min1 = parseInt(parts1.find((p) => p.type === 'minute')?.value || '0', 10);

    const hour2 = parseInt(parts2.find((p) => p.type === 'hour')?.value || '0', 10);
    const min2 = parseInt(parts2.find((p) => p.type === 'minute')?.value || '0', 10);

    const totalMinutes1 = hour1 * 60 + min1;
    const totalMinutes2 = hour2 * 60 + min2;

    let diffMinutes = Math.abs(totalMinutes1 - totalMinutes2);
    // Wrap around 24 hours if difference spans across midnight
    if (diffMinutes > 12 * 60) {
      diffMinutes = 24 * 60 - diffMinutes;
    }

    return Math.round((diffMinutes / 60) * 10) / 10;
  } catch {
    // If timezone string parsing fails, assume 0
    return 0;
  }
}

/**
 * Calculates timezone & operating window overlap (Weight: 25%).
 */
export function calculateTimezoneScore(
  studentTimezone?: string | null,
  tutorTimezone?: string | null,
  preferredDays?: number[],
  tutorAvailabilitySlots?: MatchmakerTutorCandidate['availability_slots']
): { score: number; offsetHours: number; detail: string } {
  const offset = calculateTimezoneOffsetHours(studentTimezone, tutorTimezone);

  let score = 100;
  if (offset === 0) {
    score = 100;
  } else if (offset <= 1.5) {
    score = 90;
  } else if (offset <= 3) {
    score = 75;
  } else if (offset <= 5) {
    score = 55;
  } else if (offset <= 7.5) {
    score = 35;
  } else {
    score = 20;
  }

  // Bonus if tutor has active slots on student's preferred days
  if (preferredDays && preferredDays.length > 0 && tutorAvailabilitySlots && tutorAvailabilitySlots.length > 0) {
    const hasSlotOnPreferredDay = tutorAvailabilitySlots.some(
      (slot) => preferredDays.includes(slot.day_of_week) && slot.status === 'available'
    );
    if (hasSlotOnPreferredDay) {
      score = Math.min(100, score + 10);
    }
  }

  const detail =
    offset === 0
      ? `Same timezone (${studentTimezone || 'IST'})`
      : `${offset} hr offset (${studentTimezone || 'Student'} vs ${tutorTimezone || 'Tutor'})`;

  return { score, offsetHours: offset, detail };
}

/**
 * Calculates active batch capacity & workload headroom (Weight: 15%).
 */
export function calculateCapacityScore(
  activeBatches: number = 0,
  maxCapacity: number = DEFAULT_MAX_BATCH_CAPACITY
): { score: number; detail: string } {
  if (activeBatches >= maxCapacity) {
    return {
      score: 0,
      detail: `At maximum capacity (${activeBatches}/${maxCapacity} batches)`,
    };
  }

  const headroomRatio = Math.max(0, 1 - activeBatches / maxCapacity);
  const score = Math.round(headroomRatio * 100);

  return {
    score,
    detail: `Capacity load: ${activeBatches}/${maxCapacity} active batches (${score}% available)`,
  };
}

/**
 * Calculates educational board & grade experience (Weight: 10%).
 */
export function calculateBoardScore(
  board?: string | null,
  educationLevel?: string | null,
  tutor?: MatchmakerTutorCandidate
): { score: number; detail: string } {
  if (!board && !educationLevel) {
    return { score: 70, detail: 'Standard board alignment' };
  }

  const boardNorm = normalizeString(board);
  const levelNorm = normalizeString(educationLevel);

  const experienceText = (tutor?.experience || [])
    .map((e) => `${e.role || ''} ${e.organization || ''}`)
    .join(' ');
  const bioText = tutor?.bio || '';
  const combinedContext = normalizeString(`${experienceText} ${bioText}`);

  let matchesBoard = false;
  let matchesLevel = false;

  if (boardNorm && combinedContext.includes(boardNorm)) {
    matchesBoard = true;
  }

  if (levelNorm && combinedContext.includes(levelNorm)) {
    matchesLevel = true;
  }

  if (matchesBoard && matchesLevel) {
    return {
      score: 100,
      detail: `Verified experience in ${board} and ${educationLevel}`,
    };
  }

  if (matchesBoard) {
    return {
      score: 85,
      detail: `Experience with ${board} curriculum`,
    };
  }

  if (matchesLevel) {
    return {
      score: 75,
      detail: `Experience teaching ${educationLevel}`,
    };
  }

  return {
    score: 50,
    detail: `General teaching qualification (unspecialized board)`,
  };
}

/**
 * Calculates tutor rating & verification bonus (Weight: 10%).
 */
export function calculateRatingScore(
  rating?: number,
  verificationStatus?: string
): { score: number; detail: string } {
  const effectiveRating = typeof rating === 'number' && rating > 0 ? rating : 4.5;
  const isVerified = verificationStatus === 'verified' || verificationStatus === 'active';

  // Base score from rating (out of 80) + verification bonus (20)
  const baseRatingScore = Math.min(80, (effectiveRating / 5.0) * 80);
  const verificationBonus = isVerified ? 20 : 0;
  const score = Math.round(baseRatingScore + verificationBonus);

  const detail = `Rating ${effectiveRating.toFixed(2)}/5.00 ${isVerified ? '(Verified)' : '(Pending Verification)'}`;

  return { score, detail };
}

/**
 * Pure Matchmaking Engine.
 * Evaluates candidate tutors against given criteria, calculates all weighted scores,
 * and returns ranked results sorted from highest to lowest compatibility.
 */
export function calculateTutorMatches(
  criteria: TutorMatchCriteria,
  candidates: MatchmakerTutorCandidate[]
): RankedTutorMatch[] {
  const excludedSet = new Set(criteria.excludeTutorIds || []);

  const results: RankedTutorMatch[] = candidates
    .filter((candidate) => !excludedSet.has(candidate.id))
    .map((candidate) => {
      // 1. Subject Score (Weight: 40%)
      const subjectResult = calculateSubjectScore(criteria.subjectName, candidate);

      // 2. Timezone Score (Weight: 25%)
      const timezoneResult = calculateTimezoneScore(
        criteria.studentTimezone,
        candidate.timezone,
        criteria.preferredDays,
        candidate.availability_slots
      );

      // 3. Capacity Score (Weight: 15%)
      const activeBatches = candidate.active_batches_count ?? 0;
      const maxCapacity = candidate.max_batch_capacity ?? DEFAULT_MAX_BATCH_CAPACITY;
      const capacityResult = calculateCapacityScore(activeBatches, maxCapacity);

      // 4. Board Score (Weight: 10%)
      const boardResult = calculateBoardScore(criteria.board, criteria.educationLevel, candidate);

      // 5. Rating Score (Weight: 10%)
      const ratingResult = calculateRatingScore(candidate.rating, candidate.verification_status);

      // Composite Weighted Score (0 - 100)
      const rawComposite =
        0.40 * subjectResult.score +
        0.25 * timezoneResult.score +
        0.15 * capacityResult.score +
        0.10 * boardResult.score +
        0.10 * ratingResult.score;

      const overallScore = Math.min(100, Math.max(0, Math.round(rawComposite)));

      // Match label determination
      let matchLabel: RankedTutorMatch['matchLabel'] = 'Low Match';
      if (overallScore >= 80) {
        matchLabel = 'High Match';
      } else if (overallScore >= 60) {
        matchLabel = 'Moderate Match';
      }

      // Build breakdown
      const breakdown: TutorMatchScoreBreakdown = {
        subjectScore: subjectResult.score,
        timezoneScore: timezoneResult.score,
        capacityScore: capacityResult.score,
        boardScore: boardResult.score,
        ratingScore: ratingResult.score,
        timezoneOffsetHours: timezoneResult.offsetHours,
        activeBatchCount: activeBatches,
        maxBatchCapacity: maxCapacity,
        subjectMatchDetail: subjectResult.detail,
        timezoneMatchDetail: timezoneResult.detail,
        capacityDetail: capacityResult.detail,
        boardDetail: boardResult.detail,
        ratingDetail: ratingResult.detail,
      };

      // Construct a concise human-readable recommendation reason
      let recommendationReason = '';
      if (subjectResult.score === 100 && timezoneResult.offsetHours === 0) {
        recommendationReason = `Direct subject alignment in same timezone with ${breakdown.activeBatchCount}/${breakdown.maxBatchCapacity} active batches.`;
      } else if (subjectResult.score === 100) {
        recommendationReason = `Direct subject match with a ${timezoneResult.offsetHours} hr timezone offset.`;
      } else if (timezoneResult.offsetHours === 0) {
        recommendationReason = `Same timezone match with relevant domain credentials.`;
      } else {
        recommendationReason = `Compatible educator profile across available slots.`;
      }

      return {
        tutorId: candidate.id,
        firstName: candidate.first_name || 'Tutor',
        lastName: candidate.last_name || '',
        email: candidate.email || '',
        profilePictureUrl: candidate.profile_picture_url,
        timezone: candidate.timezone,
        overallScore,
        matchLabel,
        breakdown,
        recommendationReason,
      };
    });

  // Sort candidates by overall score descending
  results.sort((a, b) => b.overallScore - a.overallScore);

  return results;
}

/**
 * Service-level helper to fetch tutors, active batch counts, and execute matchmaking.
 * Callable anywhere across the application (on-demand).
 */
export async function fetchAndCalculateTutorMatches(
  criteria: TutorMatchCriteria
): Promise<RankedTutorMatch[]> {
  try {
    // 1. Fetch tutors joined with profile info
    const { data: tutorsData, error: tutorsError } = await supabase
      .from('tutors')
      .select(`
        id,
        subjects_taught,
        bio,
        verification_status,
        rating,
        education,
        experience,
        profiles:id (
          first_name,
          last_name,
          email,
          profile_picture_url,
          timezone
        )
      `);

    if (tutorsError) {
      console.error('Error fetching tutors for matchmaking:', tutorsError);
      throw tutorsError;
    }

    // 2. Fetch active batch count grouped by default_tutor_id
    const { data: batchesData, error: batchesError } = await supabase
      .from('batches')
      .select('default_tutor_id')
      .eq('status', 'active');

    if (batchesError) {
      console.warn('Warning: Could not fetch active batch counts, defaulting load to 0:', batchesError);
    }

    const batchCountMap = new Map<string, number>();
    (batchesData || []).forEach((b) => {
      if (b.default_tutor_id) {
        batchCountMap.set(b.default_tutor_id, (batchCountMap.get(b.default_tutor_id) || 0) + 1);
      }
    });

    // 3. Format candidates for matchmaking
    const candidates: MatchmakerTutorCandidate[] = (tutorsData || []).map((t: any) => {
      const profile = Array.isArray(t.profiles) ? t.profiles[0] : t.profiles;
      return {
        id: t.id,
        first_name: profile?.first_name || '',
        last_name: profile?.last_name || '',
        email: profile?.email || '',
        profile_picture_url: profile?.profile_picture_url,
        timezone: profile?.timezone,
        subjects_taught: t.subjects_taught,
        bio: t.bio,
        verification_status: t.verification_status,
        rating: t.rating,
        education: t.education,
        experience: t.experience,
        active_batches_count: batchCountMap.get(t.id) || 0,
        max_batch_capacity: DEFAULT_MAX_BATCH_CAPACITY,
      };
    });

    // 4. Calculate matches
    return calculateTutorMatches(criteria, candidates);
  } catch (error) {
    console.error('Failed to execute tutor matchmaking:', error);
    throw error;
  }
}

